import { BaseComponent, ComponentConfig } from './base-component';
import { triggerEvent } from './util/event-handler';

const ANIMATION_DURATION = 150;
const EVENT_SHOW = 'antumo:tooltip:show';
const EVENT_SHOWN = 'antumo:tooltip:shown';
const EVENT_HIDE = 'antumo:tooltip:hide';
const EVENT_HIDDEN = 'antumo:tooltip:hidden';
const CLASS_VISIBLE = 'show';
const CLASS_TRIGGER = 'tooltip-trigger';
const CLASS_HELP_ICON_OPEN = 'tooltip-help-icon-open';
// Minimal gap between a tooltip and the viewport edge, and between the arrow and the tooltip's corner.
const VIEWPORT_MARGIN = 8;
const ARROW_EDGE = 12;
const PLACEMENT_CLASSES = ['tooltip-top', 'tooltip-topLeft', 'tooltip-topRight', 'tooltip-bottom', 'tooltip-left', 'tooltip-right'] as const;
const FOCUSABLE_TAGS = new Set(['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA']);

let tooltipIdCounter = 0;

function generateTooltipId(): string {
  tooltipIdCounter += 1;
  return `antumo-tooltip-${tooltipIdCounter}`;
}

type Placement = 'top' | 'topLeft' | 'topRight' | 'bottom' | 'left' | 'right';

export class Tooltip extends BaseComponent {
  static get NAME() { return 'tooltip'; }

  static DefaultConfig: ComponentConfig = {
    placement: 'top',
    delay: 0,
    hideDelay: 0,
    offset: 8,
  };

  private _isShown = false;
  private _tooltipElement: HTMLElement | null = null;
  private _isDynamic = false;
  private _tooltipText: string | null = null;
  private _showTimer: ReturnType<typeof setTimeout> | null = null;
  private _hideTimer: ReturnType<typeof setTimeout> | null = null;
  private _completeHideTimer: ReturnType<typeof setTimeout> | null = null;
  private _boundHandleKeydown: ((e: KeyboardEvent) => void) | null = null;

  constructor(element: Element, config: ComponentConfig = {}) {
    super(element, config);

    // Return early if getInstance returned existing (BaseComponent pattern)
    if (this._element !== element) return;

    const trigger = element as HTMLElement;

    // Determine mode: simple text vs rich tooltip
    const tooltipText = trigger.getAttribute('data-antumo-tooltip');
    if (tooltipText !== null) {
      // Simple text mode — will create element dynamically on show
      this._tooltipText = tooltipText;
      this._isDynamic = true;
    } else {
      // Rich tooltip mode — find existing element
      const targetSelector = trigger.getAttribute('data-antumo-target');
      if (targetSelector) {
        this._tooltipElement = document.querySelector(targetSelector);
      }
    }

    // Add trigger class
    trigger.classList.add(CLASS_TRIGGER);

    // Ensure trigger is focusable
    if (!FOCUSABLE_TAGS.has(trigger.tagName) && !trigger.hasAttribute('tabindex')) {
      trigger.setAttribute('tabindex', '0');
    }

    // Bind keydown handler
    this._boundHandleKeydown = this._handleKeydown.bind(this);
  }

  get tooltipElement(): HTMLElement | null {
    return this._tooltipElement;
  }

  get isShown(): boolean {
    return this._isShown;
  }

  show(): void {
    if (!this._element) return;

    // Cancel any pending hide
    this._clearHideTimer();

    // Mid-transition re-entry: pending _completeHide → restore visibility.
    if (this._completeHideTimer !== null) {
      clearTimeout(this._completeHideTimer);
      this._completeHideTimer = null;
      if (this._tooltipElement) {
        this._tooltipElement.classList.add(CLASS_VISIBLE);
        const trigger = this._element as HTMLElement;
        if (this._tooltipElement.id) {
          trigger.setAttribute('aria-describedby', this._tooltipElement.id);
        }
        if (trigger.classList.contains('tooltip-help-icon')) {
          trigger.classList.add(CLASS_HELP_ICON_OPEN);
        }
        return;
      }
      this._isShown = false;
    }

    if (this._isShown) return;

    const delay = this._config.delay as number;
    if (delay > 0 && !this._showTimer) {
      this._showTimer = setTimeout(() => {
        this._showTimer = null;
        this._doShow();
      }, delay);
      return;
    }

    this._doShow();
  }

  hide(): void {
    if (!this._isShown || !this._element) return;

    // Cancel any pending show
    this._clearShowTimer();

    const delay = this._config.hideDelay as number;
    if (delay > 0 && !this._hideTimer) {
      this._hideTimer = setTimeout(() => {
        this._hideTimer = null;
        this._doHide();
      }, delay);
      return;
    }

    this._doHide();
  }

  toggle(): void {
    if (this._isShown) {
      this.hide();
    } else {
      this.show();
    }
  }

  dispose(): void {
    this._clearShowTimer();
    this._clearHideTimer();
    if (this._completeHideTimer !== null) {
      clearTimeout(this._completeHideTimer);
      this._completeHideTimer = null;
    }

    if (this._isShown) {
      this._forceHide();
    }

    // Remove dynamic tooltip from DOM
    if (this._isDynamic && this._tooltipElement) {
      this._tooltipElement.remove();
      this._tooltipElement = null;
    }

    if (this._element) {
      const trigger = this._element as HTMLElement;
      trigger.classList.remove(CLASS_TRIGGER);
      trigger.removeAttribute('aria-describedby');
    }

    this._boundHandleKeydown = null;
    super.dispose();
  }

  private _doShow(): void {
    if (!this._element) return;

    const trigger = this._element as HTMLElement;

    const showEvent = triggerEvent(trigger, EVENT_SHOW, {}, true);
    if (showEvent.defaultPrevented) return;

    // Create or get tooltip element
    if (this._isDynamic) {
      this._tooltipElement = this._createTooltipElement();
      document.body.appendChild(this._tooltipElement);
    }

    if (!this._tooltipElement) return;

    // Set ARIA
    if (!this._tooltipElement.id) {
      this._tooltipElement.id = generateTooltipId();
    }
    this._tooltipElement.setAttribute('role', 'tooltip');
    trigger.setAttribute('aria-describedby', this._tooltipElement.id);

    // Position and apply placement
    this._positionTooltip();

    // Show
    this._tooltipElement.classList.add(CLASS_VISIBLE);

    // Toggle help icon state
    if (trigger.classList.contains('tooltip-help-icon')) {
      trigger.classList.add(CLASS_HELP_ICON_OPEN);
    }

    // Listen for keydown
    if (this._boundHandleKeydown) {
      document.addEventListener('keydown', this._boundHandleKeydown);
    }

    this._isShown = true;

    triggerEvent(trigger, EVENT_SHOWN, {}, false);
  }

  private _doHide(): void {
    if (!this._element || !this._tooltipElement) return;
    if (this._completeHideTimer !== null) return;

    const trigger = this._element as HTMLElement;

    const hideEvent = triggerEvent(trigger, EVENT_HIDE, {}, true);
    if (hideEvent.defaultPrevented) return;

    this._tooltipElement.classList.remove(CLASS_VISIBLE);

    if (trigger.classList.contains('tooltip-help-icon')) {
      trigger.classList.remove(CLASS_HELP_ICON_OPEN);
    }

    const delay = this._isReducedMotion() ? 0 : ANIMATION_DURATION;

    this._completeHideTimer = setTimeout(() => {
      this._completeHideTimer = null;
      this._completeHide();
    }, delay);
  }

  private _completeHide(): void {
    if (!this._element) return;

    const trigger = this._element as HTMLElement;

    // Remove ARIA
    trigger.removeAttribute('aria-describedby');

    // Remove dynamic element from DOM
    if (this._isDynamic && this._tooltipElement) {
      this._tooltipElement.remove();
      this._tooltipElement = null;
    }

    // Remove keydown listener
    if (this._boundHandleKeydown) {
      document.removeEventListener('keydown', this._boundHandleKeydown);
    }

    this._isShown = false;

    triggerEvent(trigger, EVENT_HIDDEN, {}, false);
  }

  private _forceHide(): void {
    if (!this._element || !this._tooltipElement) return;

    if (this._completeHideTimer !== null) {
      clearTimeout(this._completeHideTimer);
      this._completeHideTimer = null;
    }

    const trigger = this._element as HTMLElement;

    this._tooltipElement.classList.remove(CLASS_VISIBLE);
    trigger.removeAttribute('aria-describedby');

    if (trigger.classList.contains('tooltip-help-icon')) {
      trigger.classList.remove(CLASS_HELP_ICON_OPEN);
    }

    if (this._isDynamic && this._tooltipElement) {
      this._tooltipElement.remove();
      this._tooltipElement = null;
    }

    if (this._boundHandleKeydown) {
      document.removeEventListener('keydown', this._boundHandleKeydown);
    }

    this._isShown = false;
  }

  private _createTooltipElement(): HTMLElement {
    const placement = this._config.placement as string;
    const tooltip = document.createElement('div');
    tooltip.classList.add('tooltip', 'tooltip-simple', `tooltip-${placement}`);

    const content = document.createElement('div');
    content.classList.add('tooltip-content');

    const heading = document.createElement('p');
    heading.classList.add('tooltip-heading');
    heading.textContent = this._tooltipText ?? '';

    content.appendChild(heading);
    tooltip.appendChild(content);

    const arrow = document.createElement('div');
    arrow.classList.add('tooltip-arrow');
    tooltip.appendChild(arrow);

    return tooltip;
  }

  private _positionTooltip(): void {
    if (!this._element || !this._tooltipElement) return;

    const trigger = this._element as HTMLElement;
    const tooltip = this._tooltipElement;
    const offset = this._config.offset as number;
    let placement = this._config.placement as Placement;

    // Get trigger position relative to viewport
    const rect = trigger.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    // Make tooltip visible off-screen briefly to measure it
    tooltip.style.top = '-9999px';
    tooltip.style.left = '-9999px';

    // Force layout to get dimensions
    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;

    // Viewport flipping
    placement = this._flipIfNeeded(placement, rect, tooltipWidth, tooltipHeight, offset);

    // Update placement class
    this._updatePlacementClass(placement);

    // Calculate position
    let top: number;
    let left: number;

    switch (placement) {
      case 'top':
      case 'topLeft':
      case 'topRight':
        top = rect.top + scrollY - tooltipHeight - offset;
        left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        top = rect.bottom + scrollY + offset;
        left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        top = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
        left = rect.left + scrollX - tooltipWidth - offset;
        break;
      case 'right':
        top = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
        left = rect.right + scrollX + offset;
        break;
      default:
        top = rect.top + scrollY - tooltipHeight - offset;
        left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
    }

    // Keep a top/bottom tooltip inside the viewport horizontally. Near the right edge (e.g. the last
    // icon button of a page header) a centred tooltip used to stick out, widening the page (horizontal
    // scrollbar on hover) and cutting the text. The box shifts in, the arrow keeps pointing at the trigger.
    const arrow = tooltip.querySelector<HTMLElement>('.tooltip-arrow');
    if (arrow) arrow.style.left = '';
    if (placement === 'top' || placement === 'bottom') {
      const minLeft = scrollX + VIEWPORT_MARGIN;
      // clientWidth excludes a vertical scrollbar; fall back to innerWidth where layout is not computed.
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const maxLeft = scrollX + viewportWidth - VIEWPORT_MARGIN - tooltipWidth;
      const clamped = Math.max(minLeft, Math.min(left, maxLeft));
      if (clamped !== left && arrow) {
        const triggerCenter = rect.left + scrollX + rect.width / 2;
        const arrowX = Math.max(ARROW_EDGE, Math.min(tooltipWidth - ARROW_EDGE, triggerCenter - clamped));
        arrow.style.left = `${arrowX}px`;
      }
      left = clamped;
    }

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  }

  private _flipIfNeeded(
    placement: Placement,
    rect: DOMRect,
    tooltipWidth: number,
    tooltipHeight: number,
    offset: number
  ): Placement {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    switch (placement) {
      case 'top':
      case 'topLeft':
      case 'topRight':
        // If tooltip would go above viewport, flip to bottom
        if (rect.top - tooltipHeight - offset < 0) {
          return 'bottom';
        }
        break;
      case 'bottom':
        // If tooltip would go below viewport, flip to top
        if (rect.bottom + tooltipHeight + offset > viewportHeight) {
          return 'top';
        }
        break;
      case 'left':
        // If tooltip would go left of viewport, flip to right
        if (rect.left - tooltipWidth - offset < 0) {
          return 'right';
        }
        break;
      case 'right':
        // If tooltip would go right of viewport, flip to left
        if (rect.right + tooltipWidth + offset > viewportWidth) {
          return 'left';
        }
        break;
    }

    return placement;
  }

  private _updatePlacementClass(placement: Placement): void {
    if (!this._tooltipElement) return;

    // Remove all placement classes
    for (const cls of PLACEMENT_CLASSES) {
      this._tooltipElement.classList.remove(cls);
    }

    // Add correct placement class
    this._tooltipElement.classList.add(`tooltip-${placement}`);
  }

  private _handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.hide();
    }
  }

  private _clearShowTimer(): void {
    if (this._showTimer !== null) {
      clearTimeout(this._showTimer);
      this._showTimer = null;
    }
  }

  private _clearHideTimer(): void {
    if (this._hideTimer !== null) {
      clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }
  }

  private _isReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

// Reset counter for tests
export function resetTooltipIdCounter(): void {
  tooltipIdCounter = 0;
}
